/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {Flex, Button, Stack, Text} from '@chakra-ui/react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import OrderWidgetCard from '../account/my-orders/order-widget-list-card'
import ItemList from '../../components/item-list'
import {ORDERS_HISTORY_SEARCH_PARAMS} from '../account/constant'

const OrderHistory = ({orders}) => {
    const {data: customer} = useCurrentCustomer()
    const searchParams = useSearchParams(ORDERS_HISTORY_SEARCH_PARAMS)
    const headingRef = useRef()
    const navigate = useNavigation()

    useEffect(() => {
        // Focus the 'Order History' header when the component mounts for accessibility
        headingRef?.current?.focus()
    }, [])

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [customer, searchParams.offset])

    const hasOrders = orders?.length > 0

    return (
        <Stack>
            <Flex justifyContent={'space-between'}>
                <Text textStyle="head-medium" ref={headingRef} fontWeight="500">
                    <FormattedMessage
                        defaultMessage="Order History"
                        id="account_order_history.title.order_history"
                    />
                </Text>
                <Button
                    as="a"
                    onClick={() => navigate('/account/orders')}
                    variant="text-button"
                    fontWeight={400}
                >
                    <Text>
                        <FormattedMessage
                            defaultMessage="see your order history"
                            id="account_wishlist.button.see_your_order_history"
                        />
                    </Text>
                </Button>
            </Flex>

            {hasOrders && (
                <Stack spacing={0}>
                    <ItemList
                        items={orders}
                        renderItem={(order, index) => (
                            <OrderWidgetCard key={order?.Id || index} order={order} />
                        )}
                    />
                </Stack>
            )}
        </Stack>
    )
}

OrderHistory.getTemplateName = () => 'account-order-history'
OrderHistory.propTypes = {
    orders: PropTypes.arrayOf(PropTypes.object).isRequired,
}

export default OrderHistory
